/**
 * Public Privacy Policy — must be publicly reachable (no auth) because
 * Apple App Store + Google Play require a working privacy URL on the
 * listing. Drafted for a NZ SaaS, aligned with the NZ Privacy Act 2020.
 *
 * Content-owner: NZ Taxation Ltd (pilot tenant / operator).
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "1 August 2026";

const SECTIONS = [
    {
        h: "1. Who we are",
        p: [
            "ClientOps is operated by NZ Taxation Limited (New Zealand company), the \"we\" throughout this policy. We are the accounting firm hosting this application for our current and prospective clients.",
            "Contact for privacy queries: privacy@clientops.nz",
        ],
    },
    {
        h: "2. What personal information we collect",
        p: [
            "Account details — your name, email address, and (where applicable) IRD number linked to your business entity.",
            "Business records you send us — receipts, invoices, bank statements, GST returns and any other documents you upload through the portal.",
            "Financial data pulled from your integrations — if you connect Xero, Prosaic, Microsoft 365 or another accounting tool, we retrieve invoices, bank balances, tax positions and related figures needed to answer the four questions the app is built around: \"can I afford this?\", \"who owes me money?\", \"what should I do today?\", \"where's my next opportunity?\".",
            "Usage information — device type, browser, IP address, and interactions with the app, used to keep the service secure and improve it.",
        ],
    },
    {
        h: "3. How we use it",
        p: [
            "To provide the accounting, tax and financial-wellbeing services you have engaged us for.",
            "To surface financial insights inside the portal — safe-to-spend, tax reserve, debtor position, debt-eradication progress.",
            "To communicate with you about your account, filings, deadlines and reminders.",
            "To meet our legal obligations under the Tax Administration Act 1994, the Financial Reporting Act 2013 and related NZ legislation.",
        ],
    },
    {
        h: "4. Who we share it with",
        p: [
            "We do not sell your information. We share it only with:",
            "• Sub-processors that operate the app on our behalf: Emergent (hosting + AI features), Anthropic (Claude models used for document extraction and \"Ask your accountant\"), MongoDB Atlas (database), Microsoft (Outlook/SharePoint email + document routing).",
            "• Your accounting integrations that you have connected, only for the fields the integration requires (e.g. Xero, Prosaic).",
            "• The New Zealand Inland Revenue Department, only where we file returns on your behalf or where legally required.",
            "All sub-processors are bound by data-protection terms and are used only for the purpose stated above.",
        ],
    },
    {
        h: "5. Where your data is stored",
        p: [
            "Data is stored in secure cloud infrastructure operated by our sub-processors. Some processing may occur outside New Zealand (for example AI inference through Anthropic or Emergent). Where data leaves New Zealand, comparable privacy safeguards apply as required by Principle 12 of the NZ Privacy Act.",
        ],
    },
    {
        h: "6. How long we keep it",
        p: [
            "Financial records are retained for at least seven years from the end of the tax year they relate to, as required by the Tax Administration Act.",
            "Documents you upload but never file remain for the life of your account plus 12 months, after which they are deleted.",
            "You may ask us to delete your account at any time; we will retain only what the law requires us to keep.",
        ],
    },
    {
        h: "7. Your rights under the NZ Privacy Act 2020",
        p: [
            "You have the right to ask for a copy of the personal information we hold about you, to have it corrected, and to complain to us or to the Office of the Privacy Commissioner (privacy.org.nz) if you believe we have not handled it properly.",
            "To exercise any of these rights, email privacy@clientops.nz. We will respond within 20 working days.",
        ],
    },
    {
        h: "8. Security",
        p: [
            "Access to the portal requires either a password with device trust or a one-time code sent to your email. Data is encrypted in transit (TLS) and at rest. Access by our staff is restricted, logged and audited.",
        ],
    },
    {
        h: "9. Children",
        p: [
            "ClientOps is a business tool. It is not directed at or intended for individuals under the age of 18.",
        ],
    },
    {
        h: "10. Changes to this policy",
        p: [
            "We may update this policy from time to time. Material changes will be notified inside the portal at least 14 days before they take effect. Continued use of the app after the effective date constitutes acceptance.",
        ],
    },
];

export default function Privacy() {
    return (
        <div className="min-h-screen bg-[#F5F4EF] text-[#0F1115]" data-testid="privacy-page">
            <header className="border-b border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-3xl px-6 py-5 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs text-[#5E636E] hover:text-[#0F1115]"
                        data-testid="privacy-back"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to ClientOps
                    </Link>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Legal</div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-6 py-12">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#5E636E] mb-3">Privacy policy</div>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                    How we handle your information
                </h1>
                <p className="mt-3 text-sm text-[#5E636E]">Last updated: {LAST_UPDATED}</p>

                <p className="mt-8 text-sm leading-relaxed text-[#0F1115]">
                    This policy explains what personal and business information ClientOps collects, why we collect it, how we protect it, and the choices you have. It sits under the NZ Privacy Act 2020 and, where applicable, the Tax Administration Act 1994.
                </p>

                <div className="mt-10 space-y-8">
                    {SECTIONS.map((s) => (
                        <section key={s.h} className="border-l-2 border-[#0E3B2E]/20 pl-5">
                            <h2 className="font-display text-lg font-semibold">{s.h}</h2>
                            <div className="mt-2 space-y-2.5 text-sm leading-relaxed text-[#0F1115]">
                                {s.p.map((line, i) => <p key={i}>{line}</p>)}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-14 rounded-lg border border-[#E4E4E7] bg-white p-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Questions?</div>
                    <p className="mt-1.5 text-sm">
                        Email <a href="mailto:privacy@clientops.nz" className="text-[#0E3B2E] underline underline-offset-4">privacy@clientops.nz</a> and we will respond within 20 working days.
                    </p>
                </div>
            </main>

            <footer className="border-t border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-3xl px-6 py-6 text-[11px] text-[#5E636E]">
                    ClientOps is operated by NZ Taxation Limited, a New Zealand company. See also our <Link to="/support" className="underline underline-offset-4">support page</Link>.
                </div>
            </footer>
        </div>
    );
}
